import React from 'react';
import { CreateUserFormProps, ICreateUserForm } from './types';
import { ArcButton, ArcCheckbox, ArcDialog, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcPasswordInput, ArcStack, ArcText } from '@src/core/ui';
import { useDisclosure } from '@src/core/hooks';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { EMPTY_VALUE, REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { useCreateUserMutation, useGetUserRolesQuery } from '@src/features/settings/redux';
import { toast } from 'react-toastify';
import { useGetUserDataQuery, UserEntity, UserRoleType } from '@src/core/features';

const CreateUserForm: React.FC<CreateUserFormProps> = () => {
  const { open, hide, show } = useDisclosure();

  const { data = {} as UserEntity } = useGetUserDataQuery();

  const { data: roles = [] } = useGetUserRolesQuery();
  const [createUser, { isLoading: isSubmitting }] = useCreateUserMutation();


  const { control, register, handleSubmit, formState: { errors } } = useForm<ICreateUserForm>({ 
    defaultValues: {
      email: EMPTY_VALUE,
      firstname: EMPTY_VALUE,
      lastname: EMPTY_VALUE,
      password: EMPTY_VALUE,
      password2: EMPTY_VALUE,
      phone: EMPTY_VALUE,
      role: EMPTY_VALUE,
      zip: EMPTY_VALUE,
      city: EMPTY_VALUE,
      transferOwnership: false,
    }
  });

  const isOwnerRole = roles.includes(UserRoleType.OWNER) && (String(data.role) === UserRoleType.OWNER || data.role === UserRoleType.OWNER as any);

  const onCreateUser: SubmitHandler<ICreateUserForm> = (form) => {
    createUser(form).unwrap().then(() => {
      hide();
      toast.success('SUCCESS');
    }).catch((error) => {
      console.error('Create user error:', error);
      toast.error('Failed to create user');
    });
  };

  return (
    <React.Fragment>
      <ArcStack direction='row' justifyContent='end'>
        <ArcButton.brand 
          onClick={show} 
          sx={{ minWidth: 200 }}> 
          Create user 
        </ArcButton.brand>
      </ArcStack>
      <ArcDialog open={open} onClose={hide}>
        <ArcStack spacing={2} component='form' onSubmit={handleSubmit(onCreateUser)}>
          <ArcOutlinedInput 
            label='Email' 
            autoComplete='off'
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
            {...register('email', 
              { required: REQUIRED_VALIDATION_TEXT }
            )} />
          <ArcStack spacing={2} direction='row'> 
            <ArcOutlinedInput 
              label='First Name'
              autoComplete='off'
              error={Boolean(errors.firstname)}
              helperText={errors.firstname?.message}
              {...register('firstname', 
                { required: REQUIRED_VALIDATION_TEXT }
              )} />
            <ArcOutlinedInput 
              label='Last Name' 
              autoComplete='off'
              error={Boolean(errors.lastname)}
              helperText={errors.lastname?.message}
              {...register('lastname', 
                { required: REQUIRED_VALIDATION_TEXT }
              )}  />
          </ArcStack>
          <Controller 
            name='role' 
            control={control} 
            rules={{ required: REQUIRED_VALIDATION_TEXT }}
            render={({ field: { value, onChange } }) => (
              <ArcOutlinedSelect 
                label='Role'
                value={value} 
                onChange={onChange}
                error={Boolean(errors.role)}
                helperText={errors.role?.message}
              >
                {roles.map((role) => (
                  <ArcMenuItem key={role} value={role}>
                    {role}
                  </ArcMenuItem>
                ))}
              </ArcOutlinedSelect>
            )}/>
          
          <ArcPasswordInput 
            label='Password' 
            autoComplete='off'
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            {...register('password', 
              { required: REQUIRED_VALIDATION_TEXT }
            )} />
          <ArcPasswordInput 
            label='Re-enter Password' 
            autoComplete='off'
            error={Boolean(errors.password2)}
            helperText={errors.password2?.message}
            {...register('password2', 
              { required: REQUIRED_VALIDATION_TEXT }
            )} />
          <ArcOutlinedInput 
            label='Phone Number' 
            autoComplete='off'
            error={Boolean(errors.phone)}
            helperText={errors.phone?.message}
            {...register('phone', 
              { required: REQUIRED_VALIDATION_TEXT }
            )} />
          <ArcOutlinedInput 
            label='Zip Code' 
            autoComplete='off'
            error={Boolean(errors.zip)}
            helperText={errors.zip?.message}
            {...register('zip', 
              { required: REQUIRED_VALIDATION_TEXT }
            )} />
          <ArcOutlinedInput 
            label='City' 
            autoComplete='off'
            error={Boolean(errors.city)}
            helperText={errors.city?.message}
            {...register('city', 
              { required: REQUIRED_VALIDATION_TEXT }
            )} />
          
          {isOwnerRole && (
            <ArcStack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
              <ArcText.h5>Transfer ownership to this admin</ArcText.h5>
              <Controller
                name='transferOwnership'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ArcCheckbox
                    color='brand'
                    checked={value || false}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            </ArcStack>
          )}
            
          <ArcButton.brand isLoading={isSubmitting} type='submit'> Create </ArcButton.brand>
        </ArcStack>
      </ArcDialog>
    </React.Fragment>
  );
};

export default CreateUserForm;